import { brand, footer } from '@/content/site'
import { useReducedMotion } from '@/lib/useReducedMotion'

import { Reveal } from './shared/Reveal'

/**
 * Decorative network mesh — pure SVG SMIL, zero JS, zero dependencies.
 * Recoloured to the four semantic node roles.
 *
 * The travelling dots are SMIL, which CSS cannot switch off, so under
 * reduce-motion they are simply not rendered — the static mesh stays.
 */
function NetworkMesh() {
  const reduced = useReducedMotion()

  const nodes = [
    { x: 8, y: 30, c: '#8AB4F8' },
    { x: 26, y: 62, c: '#5B9DF5' },
    { x: 44, y: 24, c: '#B98CF0' },
    { x: 62, y: 58, c: '#38D0E8' },
    { x: 80, y: 30, c: '#5B9DF5' },
    { x: 92, y: 66, c: '#8AB4F8' },
  ]

  const edges: Array<[number, number]> = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 4],
    [4, 5],
    [1, 3],
    [3, 5],
  ]

  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {edges.map(([a, b], i) => {
        const p1 = nodes[a]
        const p2 = nodes[b]
        const d = `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y}`
        return (
          <g key={i}>
            <line
              x1={p1.x}
              y1={p1.y}
              x2={p2.x}
              y2={p2.y}
              stroke="#2FBF8F"
              strokeOpacity="0.16"
              strokeWidth="0.25"
              vectorEffect="non-scaling-stroke"
            />
            {!reduced && (
              <circle r="0.5" fill={p1.c} opacity="0.7">
                <animateMotion dur={`${5 + i * 0.6}s`} repeatCount="indefinite" path={d} />
                <animate
                  attributeName="opacity"
                  values="0.4;0.15;0.4"
                  dur={`${5 + i * 0.6}s`}
                  repeatCount="indefinite"
                />
              </circle>
            )}
          </g>
        )
      })}

      {nodes.map((n, i) => (
        <circle key={i} cx={n.x} cy={n.y} r="0.6" fill={n.c} opacity="0.5" />
      ))}
    </svg>
  )
}

function Wordmark() {
  return (
    <span className="font-sans text-base font-bold tracking-tight text-ink select-none">
      <span className="text-ink/30">/</span>
      {brand.wordmark}
    </span>
  )
}

export function FooterSection() {
  return (
    <footer className="footer-section relative overflow-hidden border-t border-hairline">
      {/* CTA band */}
      <div className="relative">
        <NetworkMesh />

        <div className="shell relative py-16 md:py-24">
          <div className="mx-auto max-w-2xl text-center">
            <Reveal>
              <h2 className="font-sans text-2xl font-bold tracking-tight md:text-4xl">
                {footer.headline}
              </h2>
            </Reveal>
            <Reveal i={1}>
              <p className="mx-auto mt-4 max-w-xl font-paragraph text-sm leading-relaxed text-ink-dim md:text-base">
                {footer.blurb}
              </p>
            </Reveal>
            <Reveal i={2}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <a href={footer.ctaPrimary.href} className="cta cta-primary focus-ring">
                  {footer.ctaPrimary.label}
                </a>
                <a href={footer.ctaSecondary.href} className="cta cta-ghost focus-ring">
                  {footer.ctaSecondary.label}
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      <div className="rule-fade" />

      {/* Link columns */}
      <div className="shell py-12 md:py-16">
        <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
          <Reveal i={0}>
            <div>
              <Wordmark />
              <p className="mt-3 max-w-[15rem] font-paragraph text-xs leading-relaxed text-ink-mute">
                {brand.description}
              </p>
            </div>
          </Reveal>

          {footer.columns.map((col, i) => (
            <Reveal key={col.title} i={i + 1}>
              <div className="footer-col">
                <h3 className="font-mono text-[0.63rem] uppercase tracking-[0.18em] text-node-output">
                  {col.title}
                </h3>
                <ul className="mt-4 space-y-2.5">
                  {col.links.map((link) => (
                    <li key={link.label}>
                      <a
                        href={link.href}
                        className="font-paragraph text-sm text-ink-dim transition-colors hover:text-ink"
                      >
                        {link.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* Legal bar */}
      <div className="border-t border-hairline">
        <div className="shell flex flex-col items-center justify-between gap-4 py-6 md:flex-row">
          <p className="font-paragraph text-xs text-ink-mute">{footer.copyright}</p>
          {/* Rendered as text, not links — there are no policy pages yet and a
              dead link is worse than a quiet label. Restore anchors when the
              pages exist. */}
          <div className="flex items-center gap-5">
            {footer.legal.map((label) => (
              <span key={label} className="font-paragraph text-xs text-ink-mute">
                {label}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}
